import React, { useRef, memo, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useSelector, useDispatch } from 'react-redux';
import TableCell from './TableCell';
import RowHeader from './RowHeader';
import ColumnHeader from './ColumnHeader';
import styles from '../payout-structures.module.css';
import { removeRow, removeColumn } from '@/redux/slices/payoutStructure';
import { Button } from 'reactstrap';
import { FaTrash } from 'react-icons/fa';

export const CELL_WIDTH = 120;
export const CELL_HEIGHT = 55;

export interface VirtualizedPayoutTableRef {
  scrollToRight: () => void;
  scrollToBottom: () => void;
}

export const VirtualizedPayoutTable = forwardRef<VirtualizedPayoutTableRef>((props, ref) => {
  const dispatch = useDispatch();
  const { playersColumns, rankRows } = useSelector((state: any) => state.payoutStructure);

  const parentRef = useRef<HTMLDivElement>(null);
  const scrollingRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      scrollToRight: () => {
        if (scrollingRef.current) {
          scrollingRef.current.scrollTo({
            left: scrollingRef.current.scrollWidth,
            behavior: 'smooth',
          });
        }
      },
      scrollToBottom: () => {
        if (scrollingRef.current) {
          scrollingRef.current.scrollTo({
            top: scrollingRef.current.scrollHeight,
            behavior: 'smooth',
          });
        }
      },
    }),
    [],
  );

  // Row virtualizer
  const rowVirtualizer = useVirtualizer({
    count: rankRows.length,
    getScrollElement: () => scrollingRef.current!,
    estimateSize: () => CELL_HEIGHT,
    overscan: 8,
  });

  // Column virtualizer
  const columnVirtualizer = useVirtualizer({
    horizontal: true,
    count: playersColumns.length,
    getScrollElement: () => scrollingRef.current!,
    estimateSize: () => CELL_WIDTH,
    overscan: 5,
  });

  const handleRemoveRow = useCallback(
    (rowIndex: number) => {
      // Don't allow removing the last row
      if (rankRows.length === 1) {
        return;
      }
      dispatch(removeRow(rowIndex));
    },
    [dispatch, rankRows.length],
  );

  const handleRemoveColumn = useCallback(
    (columnIndex: number) => {
      // Don't allow removing the last column
      if (playersColumns.length === 1) {
        return;
      }
      dispatch(removeColumn(columnIndex));
    },
    [dispatch, playersColumns.length],
  );

  return (
    <div ref={parentRef} className={styles.virtualizedTableContainer} style={{ position: 'relative' }}>
      {/* Corner Header - Completely Sticky */}
      <div
        className={styles.stickyCornerHeader}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: CELL_WIDTH,
          height: CELL_HEIGHT * 2,
          zIndex: 20,
        }}
      >
        <div
          className={styles.cornerHeaderCell}
          style={{
            height: CELL_HEIGHT,
            width: CELL_WIDTH,
          }}
        >
          Players:
        </div>
        <div
          className={styles.cornerHeaderCell}
          style={{
            height: CELL_HEIGHT,
            width: CELL_WIDTH,
          }}
        >
          Paid:
        </div>
      </div>

      <div
        ref={scrollingRef}
        className={styles.singleScrollContainer}
        style={{
          height: 'calc(100vh - 300px)',
          width: '100%',
        }}
      >
        {/* Column Headers - Sticky top */}
        <div
          style={{
            position: 'sticky',
            top: 0,
            left: 0,
            zIndex: 40,
            marginLeft: CELL_WIDTH,
            width: columnVirtualizer.getTotalSize(),
            height: CELL_HEIGHT * 2,
          }}
        >
          <div
            className={styles.stickyColumnHeaders}
            style={{
              position: 'relative',
              width: '100%',
              height: '100%',
            }}
          >
            {columnVirtualizer.getVirtualItems().map((virtualColumn) => (
              <div
                key={virtualColumn.index}
                className={styles.stickyColumnHeader}
                style={{
                  position: 'absolute',
                  top: 0,
                  width: virtualColumn.size,
                  transform: `translateX(${virtualColumn.start}px)`,
                }}
              >
                <ColumnHeader columnIndex={virtualColumn.index} />
                {/* Trash button superimposed on column header */}
                {playersColumns.length > 1 && (
                  <Button
                    size="sm"
                    color="danger"
                    className={styles.showOnHover}
                    onClick={() => handleRemoveColumn(virtualColumn.index)}
                    style={{
                      position: 'absolute',
                      top: '2px',
                      right: '2px',
                      width: '20px',
                      height: '20px',
                      padding: '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '10px',
                      opacity: 0.7,
                    }}
                    tabIndex={0}
                    aria-label={`Remove column ${virtualColumn.index + 1}`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleRemoveColumn(virtualColumn.index);
                      }
                    }}
                  >
                    <FaTrash />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Row Headers - Sticky left */}
        <div
          style={{
            position: 'sticky',
            left: 0,
            top: 0,
            zIndex: 10,
            marginTop: CELL_HEIGHT,
            width: CELL_WIDTH,
            height: rowVirtualizer.getTotalSize(),
          }}
        >
          <div
            className={styles.stickyRowHeaders}
            style={{
              position: 'relative',
              width: '100%',
              height: '100%',
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => (
              <div
                key={virtualRow.index}
                className={styles.stickyRowHeader}
                style={{
                  position: 'absolute',
                  width: CELL_WIDTH,
                  height: virtualRow.size,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                <RowHeader rowIndex={virtualRow.index} />
                {/* Trash button superimposed on row header */}
                {rankRows.length > 1 && (
                  <Button
                    size="sm"
                    color="danger"
                    className={styles.showOnHover}
                    onClick={() => handleRemoveRow(virtualRow.index)}
                    style={{
                      position: 'absolute',
                      top: '2px',
                      right: '2px',
                      width: '20px',
                      height: '20px',
                      padding: '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '10px',
                      opacity: 0.7,
                    }}
                    tabIndex={0}
                    aria-label={`Remove row ${virtualRow.index + 1}`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleRemoveRow(virtualRow.index);
                      }
                    }}
                  >
                    <FaTrash />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Main Data Grid */}
        <div
          className={styles.mainDataGrid}
          style={{
            left: CELL_WIDTH,
            top: CELL_HEIGHT * 3,
            width: columnVirtualizer.getTotalSize(),
            height: rowVirtualizer.getTotalSize(),
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) =>
            columnVirtualizer.getVirtualItems().map((virtualColumn) => (
              <div
                key={`${virtualRow.index}-${virtualColumn.index}`}
                className={styles.dataCell}
                style={{
                  width: virtualColumn.size,
                  height: virtualRow.size,
                  transform: `translateX(${virtualColumn.start}px) translateY(${virtualRow.start}px)`,
                }}
              >
                <TableCell rowIndex={virtualRow.index} columnIndex={virtualColumn.index} />
              </div>
            )),
          )}
        </div>
      </div>
    </div>
  );
});

VirtualizedPayoutTable.displayName = 'VirtualizedPayoutTable';

export default VirtualizedPayoutTable;
